import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Grid, Alert, Divider,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails, IconButton,
  Checkbox, FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import config from './config';

// Cadastro do modelo da folha de entrega, por cartório.
// O que for deixado em branco cai no padrão do backend (folha.py): a base legal
// é resolvida pela UF e o texto da declaração usa TEXTO_DECLARACAO_PADRAO.
const CAMPOS = [
  { k: 'nome_cartorio', label: 'Nome do cartório', obrigatorio: true, xs: 12 },
  { k: 'cns', label: 'CNS', xs: 6 },
  { k: 'uf', label: 'UF', xs: 3, maxLength: 2 },
  { k: 'municipio', label: 'Município (local da folha)', xs: 12 },
  { k: 'endereco_completo', label: 'Endereço do cabeçalho', xs: 12, linhas: 2 },
  { k: 'nome_responsavel', label: 'Responsável que assina (opcional)', xs: 12 },
  { k: 'titulo_folha', label: 'Título da folha', xs: 12 },
];

// Rótulos e seções que cada cartório ajusta sem escrever HTML.
const CAMPOS_LAYOUT = [
  { k: 'rotulo_declarante', label: 'Título da 1ª seção', placeholder: 'Dados do Proprietário' },
  { k: 'rotulo_valor_sugerido', label: 'Rótulo do valor estimado', placeholder: 'Valor Sugerido' },
];

const FLAGS = [
  { k: 'mostrar_valor_considerado', label: 'Mostrar a linha "Valor Considerado" (valor declarado pela parte)' },
  { k: 'mostrar_cabecalho_direito', label: 'Mostrar o bloco de identificação no canto superior direito' },
  { k: 'declaracao_sempre', label: 'Declaração em toda folha (e não só quando o valor declarado fica abaixo do limite)' },
];

const VAZIO = {
  nome_cartorio: '', cns: '', uf: '', municipio: '', endereco_completo: '',
  nome_responsavel: '', titulo_folha: '', texto_base_legal: '',
  texto_declaracao: '', logo_base64: '', html_template: '',
  rotulo_declarante: '', rotulo_valor_sugerido: '',
};

const VAZIO_FLAGS = {
  mostrar_valor_considerado: true,
  mostrar_cabecalho_direito: true,
  declaracao_sempre: false,
};

const FolhaPanel = ({ onClose }) => {
  const [modelo, setModelo] = useState({ ...VAZIO, ...VAZIO_FLAGS });
  const [baseLegalEfetiva, setBaseLegalEfetiva] = useState('');
  const [cadastrado, setCadastrado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${config.API_URL}/folha/modelo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModelo({
        ...VAZIO,
        ...VAZIO_FLAGS,
        ...Object.fromEntries(
          Object.entries(data).filter(([k, v]) => k in VAZIO && v != null)
        ),
        ...Object.fromEntries(
          Object.keys(VAZIO_FLAGS).map((k) => [k, !!data[k]])
        ),
      });
      setBaseLegalEfetiva(data.base_legal_efetiva || '');
      setCadastrado(!!data.cadastrado);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao carregar o modelo da folha.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const set = (k) => (e) => {
    const v = k === 'uf' ? e.target.value.toUpperCase().slice(0, 2) : e.target.value;
    setModelo((m) => ({ ...m, [k]: v }));
    setOk(null);
  };

  const carregarLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) {
      setErro('Logo acima de 300 KB — o arquivo vai junto em toda folha gerada.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setModelo((m) => ({ ...m, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const salvar = async () => {
    if (!modelo.nome_cartorio.trim()) {
      setErro('O nome do cartório é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const token = localStorage.getItem('token');
      const corpo = Object.fromEntries(
        Object.entries(modelo).map(([k, v]) => [k, v === '' ? null : v])
      );
      corpo.nome_cartorio = modelo.nome_cartorio;
      const { data } = await axios.put(`${config.API_URL}/folha/modelo`, corpo, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBaseLegalEfetiva(data.base_legal_efetiva || '');
      setCadastrado(true);
      setOk('Modelo salvo. As próximas folhas já saem com estes dados.');
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao salvar o modelo.');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ overflow: 'auto', p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <DescriptionIcon sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ flex: 1 }}>Modelo da Folha de Entrega</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {!cadastrado && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Este cartório ainda não tem modelo próprio. Até salvar, a folha sai com o
          cabeçalho genérico e a base legal padrão.
        </Alert>
      )}
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk(null)}>{ok}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Cabeçalho</Typography>
        <Grid container spacing={2}>
          {CAMPOS.map((c) => (
            <Grid item xs={12} sm={c.xs} key={c.k}>
              <TextField
                fullWidth size="small" label={c.label}
                value={modelo[c.k] || ''} onChange={set(c.k)}
                required={c.obrigatorio}
                multiline={!!c.linhas} minRows={c.linhas || 1}
                inputProps={c.maxLength ? { maxLength: c.maxLength } : undefined}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button variant="outlined" component="label" size="small">
              {modelo.logo_base64 ? 'Trocar logo' : 'Enviar logo'}
              <input hidden type="file" accept="image/*" onChange={carregarLogo} />
            </Button>
            {modelo.logo_base64 && (
              <>
                <Box component="img" src={modelo.logo_base64} alt="logo"
                     sx={{ height: 48, ml: 2, verticalAlign: 'middle' }} />
                <Button size="small" sx={{ ml: 1 }}
                        onClick={() => setModelo((m) => ({ ...m, logo_base64: '' }))}>
                  remover
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Rótulos e seções</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          {CAMPOS_LAYOUT.map((c) => (
            <Grid item xs={12} sm={6} key={c.k}>
              <TextField
                fullWidth size="small" label={c.label}
                placeholder={c.placeholder}
                value={modelo[c.k] || ''} onChange={set(c.k)}
                helperText={`Em branco = "${c.placeholder}"`}
              />
            </Grid>
          ))}
        </Grid>
        {FLAGS.map((f) => (
          <FormControlLabel
            key={f.k}
            sx={{ display: 'block' }}
            control={(
              <Checkbox
                checked={!!modelo[f.k]}
                onChange={(e) => {
                  setModelo((m) => ({ ...m, [f.k]: e.target.checked }));
                  setOk(null);
                }}
              />
            )}
            label={<Typography variant="body2">{f.label}</Typography>}
          />
        ))}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Textos</Typography>
        <TextField
          fullWidth size="small" multiline minRows={4} sx={{ mb: 2 }}
          label="Base legal (em branco = texto padrão da UF)"
          value={modelo.texto_base_legal || ''} onChange={set('texto_base_legal')}
          helperText={
            modelo.texto_base_legal
              ? 'Texto próprio deste cartório.'
              : `Padrão em uso para ${modelo.uf || 'UF não informada'}: ${
                  (baseLegalEfetiva || '').slice(0, 120)}...`
          }
        />
        <TextField
          fullWidth size="small" multiline minRows={4}
          label="Declaração de ciência (em branco = texto padrão)"
          value={modelo.texto_declaracao || ''} onChange={set('texto_declaracao')}
          helperText="Só aparece na folha quando o valor declarado fica abaixo do limite inferior."
        />
      </Paper>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">HTML próprio (avançado)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            Preenchendo aqui, o backend ignora o layout padrão e usa este HTML.
            Placeholders aceitos: <code>{'{{nomeCliente}} {{cpfCliente}} {{enderecoCliente}} '
            + '{{nomeCartorio}} {{cns}} {{dadosCliente}} {{valorInformado}} '
            + '{{valor_venda_media}} {{observacoes}} {{id_calculo}} {{nome_municipio}} '
            + '{{dataGeracao}} {{baseLegal}} {{textoDeclaracao}}'}</code>.
            Nesse modo sai uma página só, com o primeiro proprietário.
          </Alert>
          <TextField
            fullWidth size="small" multiline minRows={8}
            label="HTML do modelo" value={modelo.html_template || ''}
            onChange={set('html_template')}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar modelo'}
        </Button>
        <Button variant="outlined" onClick={carregar} disabled={salvando}>
          Descartar alterações
        </Button>
      </Box>
    </Box>
  );
};

export default FolhaPanel;
